import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Bàn dựng nhiều lớp cho Cab Studio — tất cả trên CÙNG một trục thời gian.
 *
 * Vì sao có: trước đây mỗi thứ một khu riêng, muốn chèn chữ ở giây thứ 7 thì phải cuộn xuống
 * gõ số vào ô "từ giây / đến giây". Sếp muốn "vừa cắt vừa chèn, đa nhiệm hơn" → nay kéo thả
 * trực tiếp trên timeline.
 *
 * Điểm quan trọng: timeline này KHÔNG phải trang trí. Nó đọc/ghi đúng những giá trị
 * start/end mà backend đã nhận và đã render (ffmpeg drawtext enable=between(t,start,end)),
 * nên kéo thả ở đây = đổi đúng kết quả video xuất ra.
 *
 * Lớp Nhạc / Giọng đọc hiện là thanh CHỈ ĐỂ XEM (backend đang trộn từ giây 0, chưa có độ trễ).
 */

const MIN_LEN = 0.3; // block ngắn nhất, để không kéo mất tiêu
const SNAP = 0.1;

const snap = (v: number) => Math.round(v / SNAP) * SNAP;
const fmt = (s: number) => `${s.toFixed(1)}s`;

export interface TlBlock {
  start: number;
  end: number;
  label: string;
}

type DragMode = 'move' | 'l' | 'r';

interface TrackProps {
  total: number;
  blocks: TlBlock[];
  color: string;
  selected: number | null;
  onSelect: (i: number) => void;
  onChange: (i: number, patch: { start?: number; end?: number }) => void;
  widthPx: number;
}

const BlockRow = ({ total, blocks, color, selected, onSelect, onChange, widthPx }: TrackProps) => {
  const drag = useRef<{ i: number; mode: DragMode; x0: number; s0: number; e0: number } | null>(null);

  const secPerPx = total > 0 && widthPx > 0 ? total / widthPx : 0;

  const onDown = (e: React.PointerEvent, i: number, mode: DragMode) => {
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = { i, mode, x0: e.clientX, s0: blocks[i].start, e0: blocks[i].end };
    onSelect(i);
  };

  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || !secPerPx) return;
    const dt = (e.clientX - d.x0) * secPerPx;

    if (d.mode === 'move') {
      const len = d.e0 - d.s0;
      let s = snap(Math.min(Math.max(d.s0 + dt, 0), Math.max(0, total - len)));
      onChange(d.i, { start: s, end: snap(s + len) });
    } else if (d.mode === 'l') {
      const s = snap(Math.min(Math.max(d.s0 + dt, 0), d.e0 - MIN_LEN));
      onChange(d.i, { start: s });
    } else {
      const en = snap(Math.max(Math.min(d.e0 + dt, total), d.s0 + MIN_LEN));
      onChange(d.i, { end: en });
    }
  };

  const onUp = () => {
    drag.current = null;
  };

  return (
    <>
      {blocks.map((b, i) => {
        const left = total > 0 ? (Math.max(0, b.start) / total) * 100 : 0;
        const w = total > 0 ? (Math.max(MIN_LEN, b.end - b.start) / total) * 100 : 0;
        const isSel = selected === i;
        return (
          <div
            key={i}
            role="button"
            tabIndex={0}
            onPointerDown={(e) => onDown(e, i, 'move')}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            title={`${b.label} · ${fmt(b.start)} → ${fmt(b.end)} (kéo để di chuyển, kéo 2 mép để đổi độ dài)`}
            className={`absolute top-1 bottom-1 rounded-md text-[10px] leading-none px-1.5 flex items-center overflow-hidden cursor-grab select-none touch-none ${
              isSel ? 'ring-2 ring-white/90 z-10' : ''
            }`}
            style={{ left: `${left}%`, width: `${w}%`, minWidth: 18, background: color }}
          >
            <span className="truncate text-white/95 pointer-events-none">{b.label}</span>
            {/* 2 mép kéo để đổi độ dài */}
            <span
              onPointerDown={(e) => onDown(e, i, 'l')}
              onPointerMove={onMove}
              onPointerUp={onUp}
              className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize bg-black/25 touch-none"
            />
            <span
              onPointerDown={(e) => onDown(e, i, 'r')}
              onPointerMove={onMove}
              onPointerUp={onUp}
              className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize bg-black/25 touch-none"
            />
          </div>
        );
      })}
    </>
  );
};

const TrackShell = ({
  name,
  hint,
  children,
}: {
  name: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-stretch gap-2">
    <div className="w-[74px] shrink-0 text-[11px] ui-muted pt-2 leading-tight">
      {name}
      {hint && <div className="text-[10px] opacity-70">{hint}</div>}
    </div>
    <div className="relative flex-1 h-9 rounded-md ui-surface-2 overflow-hidden">{children}</div>
  </div>
);

export interface TimelineProps {
  total: number;
  clips: { duration: number; start: number; end: number }[];
  selectedClip: number | null;
  onSelectClip: (i: number) => void;

  texts: { text: string; start: number; end: number }[];
  selectedText: number | null;
  onSelectText: (i: number) => void;
  onChangeText: (i: number, patch: { start?: number; end?: number }) => void;

  effects: { type: string; text: string; start: number; end: number }[];
  selectedEffect: number | null;
  onSelectEffect: (i: number) => void;
  onChangeEffect: (i: number, patch: { start?: number; end?: number }) => void;

  effectLabel: (type: string) => string;
  musicLabel?: string | null;
  voiceLabel?: string | null;
}

export const Timeline = ({
  total,
  clips,
  selectedClip,
  onSelectClip,
  texts,
  selectedText,
  onSelectText,
  onChangeText,
  effects,
  selectedEffect,
  onSelectEffect,
  onChangeEffect,
  effectLabel,
  musicLabel,
  voiceLabel,
}: TimelineProps) => {
  const areaRef = useRef<HTMLDivElement>(null);
  const [widthPx, setWidthPx] = useState(0);

  // Cần độ rộng thật (px) để đổi khoảng kéo chuột -> số giây.
  const measure = useCallback(() => {
    setWidthPx(areaRef.current?.clientWidth ?? 0);
  }, []);

  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  if (clips.length === 0) return null;

  // Mốc thời gian trên thước: chọn bước sao cho ra khoảng 6-10 vạch.
  const step = total <= 12 ? 2 : total <= 30 ? 5 : total <= 90 ? 10 : 30;
  const ticks: number[] = [];
  for (let t = 0; t <= total + 0.001; t += step) ticks.push(Number(t.toFixed(1)));

  // Lớp video: các clip xếp nối tiếp, độ rộng = đoạn được giữ (đúng như video xuất ra).
  let acc = 0;
  const clipBlocks = clips.map((c, i) => {
    const len = Math.max(0, c.end - c.start);
    const b = { start: acc, end: acc + len, label: `Clip ${i + 1}` };
    acc += len;
    return b;
  });

  return (
    <div>
      {/* Thước thời gian */}
      <div className="flex items-stretch gap-2 mb-1">
        <div className="w-[74px] shrink-0" />
        <div ref={areaRef} className="relative flex-1 h-4">
          {ticks.map((t) => (
            <div
              key={t}
              className="absolute top-0 text-[10px] ui-muted"
              style={{ left: `${total > 0 ? (t / total) * 100 : 0}%`, transform: 'translateX(-50%)' }}
            >
              {t}s
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <TrackShell name="Video" hint={`${clips.length} clip`}>
          {clipBlocks.map((b, i) => {
            const left = total > 0 ? (b.start / total) * 100 : 0;
            const w = total > 0 ? ((b.end - b.start) / total) * 100 : 0;
            return (
              <button
                key={i}
                type="button"
                onClick={() => onSelectClip(i)}
                title={`${b.label} · ${fmt(b.end - b.start)} — bấm để cắt clip này`}
                className={`absolute top-1 bottom-1 rounded-md text-[10px] px-1.5 text-white/95 overflow-hidden ${
                  selectedClip === i ? 'ring-2 ring-white/90 z-10' : ''
                }`}
                style={{ left: `${left}%`, width: `${w}%`, minWidth: 18, background: '#0284c7' }}
              >
                <span className="truncate block">{b.label}</span>
              </button>
            );
          })}
        </TrackShell>

        <TrackShell name="Chữ" hint={texts.length ? `${texts.length} mục` : 'chưa có'}>
          <BlockRow
            total={total}
            widthPx={widthPx}
            color="#7c3aed"
            blocks={texts.map((t, i) => ({
              start: t.start,
              end: t.end,
              label: t.text?.trim() || `Chữ ${i + 1}`,
            }))}
            selected={selectedText}
            onSelect={onSelectText}
            onChange={onChangeText}
          />
        </TrackShell>

        <TrackShell name="Hiệu ứng" hint={effects.length ? `${effects.length} mục` : 'chưa có'}>
          <BlockRow
            total={total}
            widthPx={widthPx}
            color="#db2777"
            blocks={effects.map((e) => ({
              start: e.start,
              end: e.end,
              label: effectLabel(e.type),
            }))}
            selected={selectedEffect}
            onSelect={onSelectEffect}
            onChange={onChangeEffect}
          />
        </TrackShell>

        {musicLabel && (
          <TrackShell name="Nhạc nền" hint="cả video">
            <div className="absolute inset-1 rounded-md bg-[#059669] text-white/95 text-[10px] px-1.5 flex items-center overflow-hidden">
              <span className="truncate">{musicLabel}</span>
            </div>
          </TrackShell>
        )}

        {voiceLabel && (
          <TrackShell name="Giọng đọc" hint="từ đầu video">
            <div className="absolute inset-1 rounded-md bg-[#d97706] text-white/95 text-[10px] px-1.5 flex items-center overflow-hidden">
              <span className="truncate">{voiceLabel}</span>
            </div>
          </TrackShell>
        )}
      </div>

      <p className="text-[11px] ui-muted mt-2">
        Kéo khối <b>Chữ</b> / <b>Hiệu ứng</b> để đổi thời điểm xuất hiện, kéo 2 mép để đổi độ dài. Bấm khối
        <b> Video</b> để chọn clip cần cắt ở Bàn cắt bên trên.
      </p>
    </div>
  );
};
